import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { AuthShell, AuthHeading, authShellCopy } from '@/features/auth-shell';
import { useAuthActions } from '@/hooks/use-auth';
import { translations } from '@/translations/translations';
import { SignupStep1 } from '@/components/auth/SignupStep1';
import { SignupStep2 } from '@/components/auth/SignupStep2';
import { useSignupGeolocation } from '@/hooks/use-signup-geolocation';
import type { SignupForm } from '@/lib/signup.constants';

const TOTAL_STEPS = 2;

export default function Signup() {
  const { isAuthenticated } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { toast } = useToast();
  const currentLang = lang || 'fr';
  const t = translations[currentLang as 'fr' | 'en'] || translations.en;
  const geo = useSignupGeolocation();

  const [step, setStep] = useState(1);
  const { register, loading } = useAuthActions();

  const [form, setForm] = useState<SignupForm>({
    username: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: '',
    gender: '',
    phoneNumber: '',
    spokenLanguages: [],
    contributionLanguages: [],
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(`/${currentLang}`, { replace: true });
    }
  }, [currentLang, isAuthenticated, navigate]);

  useEffect(() => {
    if (geo && !form.country) {
      setForm((prev) => ({
        ...prev,
        country: geo.countryName,
        phoneNumber: geo.dial ? `${geo.dial} ` : prev.phoneNumber,
      }));
    }
  }, [geo]);

  const updateForm = (fields: Partial<SignupForm>) => {
    setForm((prev) => ({ ...prev, ...fields }));
  };

  const handleNextStep = () => {
    if (!form.username.trim() || !form.name.trim()) {
      toast({
        title: t.requiredFieldsTitle,
        description: t.signupIdentityRequired,
        variant: 'destructive',
      });
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNextStep();
      return;
    }
    if (!form.country) {
      toast({
        title: t.requiredFieldsTitle,
        description: t.signupCountryRequired,
        variant: 'destructive',
      });
      return;
    }
    if (!form.gender) {
      toast({
        title: t.requiredFieldsTitle,
        description: t.signupGenderRequired,
        variant: 'destructive',
      });
      return;
    }
    if (
      form.spokenLanguages.length === 0 ||
      form.contributionLanguages.length === 0
    ) {
      toast({
        title: t.requiredFieldsTitle,
        description: t.languagePreferencesRequired,
        variant: 'destructive',
      });
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast({ title: t.passwordsDoNotMatch, variant: 'destructive' });
      return;
    }
    try {
      await register({
        ...form,
        preferredLocale: currentLang === 'en' ? 'en' : 'fr',
      });
      navigate(`/${currentLang}`, { replace: true });
    } catch (err) {
      console.error(err);
    }
  };

  const shellCopy = authShellCopy(currentLang);
  const stepLabels = [t.stepAccount, t.stepProfile];

  return (
    <AuthShell documentTitle={t.createAccount}>
      <AuthHeading
        title={t.createAccount}
        description={t.authContinueContribution}
      />

      {/* Progression : deux segments, l'étape en cours est annoncée. */}
      <nav aria-label={shellCopy.stepProgress(step, TOTAL_STEPS)} data-testid="signup-steps">
        <p className="mb-2.5 text-xs font-medium text-muted-foreground">{shellCopy.stepProgress(step, TOTAL_STEPS)}</p>
        <ol className="grid grid-cols-2 gap-2">
          {stepLabels.map((label, index) => {
            const s = index + 1;
            return (
              <li key={label} aria-current={step === s ? 'step' : undefined}>
                <span
                  className={`block h-1 rounded-full transition-colors duration-300 ${step >= s ? 'bg-primary' : 'bg-border'}`}
                />
                <span className={`mt-2 block text-xs ${step >= s ? 'text-foreground' : 'text-faint-foreground'}`}>
                  {s}. {label}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form" data-step={step}>
        {step === 1 && <SignupStep1 form={form} onUpdate={updateForm} t={t} />}
        {step === 2 && (
          <SignupStep2
            form={form}
            onUpdate={updateForm}
            t={t}
            lang={currentLang}
            loading={loading}
            onBack={() => setStep(1)}
            geo={geo}
          />
        )}
      </form>

      <p className="border-t border-border/70 pt-6 text-center text-sm text-muted-foreground">
        {t.alreadyMember}{' '}
        <Link to={`/${lang || 'fr'}/login`} className="rounded font-semibold text-brand hover:underline focus-ring">
          {t.login}
        </Link>
      </p>
    </AuthShell>
  );
}
